<script lang="ts" setup>
import { md, sm } from "~/utils/breakpoints"

withDefaults(
  defineProps<{
    showAvailability: boolean
    activeCookieName: string
    showQuery: boolean
    hasResultsNumberUnderFilters: boolean
  }>(),
  {
    showAvailability: true,
    activeCookieName: "",
    showQuery: true,
    hasResultsNumberUnderFilters: false
  }
)

const { isOpen, switchState: openSorting } = useUIState(SORTING_MODAL)
const { isOpen: isFiltersModalOpen, switchState: openFilters } =
  useUIState(FILTERS_MODAL)

const { nbHits, getFiltersNumbersByState } = useSwiftSearchHelper()

const getFiltersNumbers = () => {
  return getFiltersNumbersByState([
    "inStock",
    "brand.name",
    "mainCategory.name",
    "deductibleProduct",
    "price.amount_float",
    "isDiscounted"
  ])
}

const mobileFiltersRef = ref()
const headerHeight: Ref<number> = useState("header-height")
const isScrollingUp: Ref<boolean> = useState("scrolling-up")

const { y: scrollFilters } = useElementBounding(mobileFiltersRef)

const isIntersecting = computed<boolean>(
  // checks is the filters are scrolling further up than the headerHeight, but only on sm breakpoints
  () =>
    md.value
      ? false // Only on mobile ..
      : scrollFilters.value <= // ..when the "mobileFiltersRef" ref element reaches:
        (isScrollingUp.value // ..and when scrolling up
          ? Math.round(headerHeight.value) //.. hide the filters when the HEADER is reached (aka "scrollFilters" ("y") - the header height)
          : 0) // .. otherwise, when scrolling down, hide the filters when it reaches the TOP of the page (aka "scrollFilters" ("y") - 0)
)

//the computed distance of the pinned filters from the top
const offsetFromTop = computed(() => `${Math.round(headerHeight.value)}px`)

const isScrollingLocked = process.client
  ? useScrollLock(document.body, false)
  : ref(false)

watch(isFiltersModalOpen, (newval) => {
  newval && sm.value
    ? (isScrollingLocked.value = true)
    : (isScrollingLocked.value = false)
})
</script>

<template>
  <div class="negative-margined col-span-full md:mx-0">
    <!-- Mobile filters -->
    <!-- Empty div to use as reference for filters scrolling -->
    <div
      ref="mobileFiltersRef"
      :class="{ 'h-[79px] md:h-0': isIntersecting }"
    ></div>
    <div
      class="md:hidden"
      :class="{
        filters__pinned: isIntersecting,
        '-translate-y-full !drop-shadow-01 transition-transform duration-300':
          !isScrollingUp && isIntersecting, // Only transitioned it when scrolling up AND already intersected (intersected where? at the header height or the top of the viewport (see above for more info))
        'transition-transform duration-300': isScrollingUp
      }"
    >
      <FiltersUtilsMobileFiltersBar
        :n-filters="getFiltersNumbers()"
        :n-results="nbHits"
        :sorting-open="isOpen"
        showResults
        @onFilter="openFilters(true)"
        @onSort="openSorting(true)"
      />
    </div>
    <AlgoliaMobileFilters />
    <AisSortBy>
      <template v-slot="{ items, currentRefinement, refine }">
        <FiltersUtilsModalsSorting
          :items="items"
          :current-active="currentRefinement"
          :active-cookie-name="activeCookieName"
          @onSelectItem="refine($event.value)"
          @onClose="openSorting(false)"
        />
      </template>
    </AisSortBy>

    <!-- Desktop filters -->
    <AlgoliaDesktopFilters
      :show-availability="showAvailability"
      :active-cookie-name="activeCookieName"
      :show-query="showQuery"
      :hasResultsNumberUnderFilters="hasResultsNumberUnderFilters"
    />
  </div>
</template>
<style scoped lang="scss">

.filters__pinned {
  @apply fixed top-0 z-[19] w-screen transform-gpu  bg-white drop-shadow-01;
  padding-top: v-bind(offsetFromTop);
}

</style>
